import { useListPassengerTransportModes } from '@shared/api/hooks/ccf/activities/business-travel';
import { useGetActivityTypeUnits } from '@shared/api/hooks/ccf/units';
import { PassengerTransportMode } from '@shared/api/types';
import { RepeatableFormRows } from '@shared/components/form/repeatable-form-rows';
import SelectFormField from '@shared/components/form/select-form-field';
import { capitalizeFirstLetter } from '@shared/lib/utils';
import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useUnitOptions } from '../../../../utils';
import BusinessTravelRecordsRow from '../business-travel-records-row';

interface DistanceFieldsProps {
  isEditMode: boolean;
  secrEligibleOnly?: boolean;
}

interface ResolvedMode {
  uuid: string;
  mode: string;
  type: string | null;
  subtype: string | null;
}

const toOption = (label: string | null | undefined) =>
  label ? { value: label, label: capitalizeFirstLetter(label) } : null;

export default function DistanceFields({
  isEditMode,
  secrEligibleOnly,
}: DistanceFieldsProps) {
  const { watch, setValue, getValues } = useFormContext();
  const site = watch('site');
  const vehicleType = watch('vehicleType') as string | undefined;
  const vehicleSize = watch('vehicleSize') as string | undefined;
  const fuelType = watch('fuelType') as string | undefined;
  const passengerTransportMode = watch('passengerTransportMode') as
    | string
    | undefined;

  const { data: modes, isLoading: isModesLoading } =
    useListPassengerTransportModes({
      site,
      ...(secrEligibleOnly ? { is_secr_eligible: true } : {}),
    });

  const modesList = (modes as PassengerTransportMode[] | undefined) ?? [];

  const vehicleTypeOptions = useMemo(() => {
    const seen = new Set<string>();
    const options: { value: string; label: string }[] = [];
    for (const m of modesList) {
      if (!seen.has(m.mode)) {
        seen.add(m.mode);
        const opt = toOption(m.mode);
        if (opt) options.push(opt);
      }
    }
    return options;
  }, [modesList]);

  const vehicleSizeOptions = useMemo(() => {
    if (!vehicleType) return [];
    const seen = new Set<string>();
    const options: { value: string; label: string }[] = [];
    for (const m of modesList) {
      if (m.mode === vehicleType && m.type && !seen.has(m.type)) {
        seen.add(m.type);
        const opt = toOption(m.type);
        if (opt) options.push(opt);
      }
    }
    return options;
  }, [modesList, vehicleType]);

  const fuelTypeOptions = useMemo(() => {
    if (!vehicleType) return [];
    const seen = new Set<string>();
    const options: { value: string; label: string }[] = [];
    for (const m of modesList) {
      if (
        m.mode === vehicleType &&
        (m.type ?? null) === (vehicleSize ?? null) &&
        m.subtype &&
        !seen.has(m.subtype)
      ) {
        seen.add(m.subtype);
        const opt = toOption(m.subtype);
        if (opt) options.push(opt);
      }
    }
    return options;
  }, [modesList, vehicleType, vehicleSize]);

  // Treat stale cascade values as undefined when they're not in the current
  // options. After switching vehicleType, the prior vehicleSize/fuelType
  // form values can linger even when the new branch has no equivalent cascade
  // level — e.g. Ferry/Car-passenger and Bus/Coach have no subtype, but a
  // prior fuelType="Hybrid" would block the find from matching their null
  // subtype. Filtering down to in-options values lets the cascade resolve as
  // soon as the user picks a valid downstream value.
  const effectiveVehicleSize = vehicleSizeOptions.some(
    (o) => o.value === vehicleSize
  )
    ? vehicleSize
    : undefined;
  const effectiveFuelType = fuelTypeOptions.some((o) => o.value === fuelType)
    ? fuelType
    : undefined;

  const resolvedMode: ResolvedMode | undefined = useMemo(() => {
    if (!vehicleType) return undefined;
    return modesList.find(
      (m) =>
        m.mode === vehicleType &&
        (m.type ?? null) === (effectiveVehicleSize ?? null) &&
        (m.subtype ?? null) === (effectiveFuelType ?? null)
    ) as ResolvedMode | undefined;
  }, [modesList, vehicleType, effectiveVehicleSize, effectiveFuelType]);

  useEffect(() => {
    const currentUuid = getValues('passengerTransportMode');
    if (resolvedMode?.uuid !== currentUuid) {
      setValue('passengerTransportMode', resolvedMode?.uuid ?? '', {
        shouldValidate: true,
      });
    }
  }, [resolvedMode, setValue, getValues]);

  // React Query v4 reports isLoading=true for a disabled query that's never
  // been fetched for the current key — which would show a phantom spinner
  // before the cascade is complete. isFetching only flips true while a real
  // request is in flight, so it's the correct signal for the visual loader.
  const { data: units, isFetching: isUnitsFetching } = useGetActivityTypeUnits(
    'business-travel',
    { site, passenger_transport_mode: passengerTransportMode },
    { enabled: !!passengerTransportMode }
  );

  const formattedUnits = useUnitOptions(units!);

  const sizeRequired = vehicleSizeOptions.length > 0;
  const fuelRequired = fuelTypeOptions.length > 0;
  const noUnitsAvailable =
    !!passengerTransportMode && !isUnitsFetching && units?.length === 0;

  return (
    <>
      <div className="grid grid-cols-12 gap-2">
        <SelectFormField
          name="vehicleType"
          label="Transport mode"
          options={vehicleTypeOptions}
          disabled={isEditMode || isModesLoading}
          className="col-span-3"
        />
        <SelectFormField
          name="vehicleSize"
          label="Transport category"
          options={vehicleSizeOptions}
          disabled={isEditMode || !vehicleType || !sizeRequired}
          placeholder={
            !vehicleType
              ? 'Select transport mode'
              : !sizeRequired
                ? 'Not applicable'
                : ''
          }
          className="col-span-3"
        />
        <SelectFormField
          name="fuelType"
          label="Transport sub-category"
          options={fuelTypeOptions}
          disabled={
            isEditMode ||
            !vehicleType ||
            (sizeRequired && !vehicleSize) ||
            !fuelRequired
          }
          placeholder={
            !vehicleType
              ? 'Select transport mode'
              : !fuelRequired
                ? 'Not applicable'
                : ''
          }
          className="col-span-3"
        />
        {!isEditMode && (
          <SelectFormField
            name="unit"
            label="Unit"
            options={formattedUnits}
            loading={isUnitsFetching}
            disabled={
              !passengerTransportMode || isUnitsFetching || noUnitsAvailable
            }
            placeholder={
              !passengerTransportMode
                ? 'Select vehicle first'
                : noUnitsAvailable
                  ? 'No units available'
                  : ''
            }
            className="col-span-3"
          />
        )}
      </div>
      <RepeatableFormRows
        name="records"
        includeErrorMessage
        showAddButton={false}
        component={BusinessTravelRecordsRow}
        newRowObject={{ uuid: '', quantity: undefined, timePeriod: '' }}
      />
    </>
  );
}
